/*
 * This file is the entry point to the VizPortal Client application. The order of imports in this
 * file is very important, because these imports all have side effects that affect how the app gets
 * initialized.
 *
 * As of fall 2020, VizPortal Client has two paradigms:
 *
 * - AngularJS (old): Each directive, controllers, and other Angular configuration registers itself
 *   into the dependency injector, so those are all listed here, and `run` (which starts the Angular
 *   app) must be last.
 *
 * - React/Redux (new): The React portion of the code is written entirely in JavaScript, so the
 *   import order matters less (because all components depend on their children via). But there are
 *   still some global initializations that must happen before the root component is rendered.
 *   (note: as Angular still exists, the root React component is currently rendered by Angular.)
 */

import './configureGlobals';
import './FeatureFlags';
import './sidecar';

import './decorators/qDecorator';

import '../httpClient/utils/Interceptors';

import '../app/InitReduxStore';
import '../contentDefinitions/populateContentDefinitionLookupLists';

import '../common/attributes/tbAutoSelect';
import '../common/attributes/tbButtonClick';
import '../common/attributes/tbButtonDown';
import '../common/attributes/tbFocus/FocusCtrl';
import '../common/attributes/tbFocus/tbFocus';
import '../common/components/checkboxes/tbCheckbox';
import '../common/components/checkboxes/tbCheckboxGroup';
import '../common/components/checkboxes/tbCheckboxLabel';
import '../common/components/checkboxes/tbCheckboxTristate';
import '../common/components/tbDatePicker/tbDatePicker';
import '../common/react/angularDirectives';
import '../common/react/components/dataGrid/AngularToReactDataGridBridge';
import '../common/react/components/InterpolatedString';
import '../common/utilities/TableauLog';

import '../settings/components/AnalyticsExtensionsSettingsForSite';
import '../settings/controllers/ServerSettingsFormCtrl';
import '../settings/controllers/SiteSettingsForAnalyticsExtensionsCtrl';
import '../settings/controllers/SiteSettingsForServerAdminFormCtrl';
import '../settings/controllers/SiteSettingsForSiteAdminFormCtrl';
import '../settings/directives/tbAnalyticsExtensionsSettingsForm';
import '../settings/directives/tbServerSettingsForm';
import '../settings/directives/tbServerSettingsFormAdapter';
import '../settings/directives/tbSettingsCoordinator';
import '../settings/directives/tbSiteSettingsForExtensionsForm';
import '../settings/directives/tbSiteSettingsForServerAdminForm';
import '../settings/directives/tbSiteSettingsForServerAdminFormAdapter';
import '../settings/directives/tbSiteSettingsForSiteAdminForm';
import '../settings/directives/tbSiteSettingsForSiteAdminFormAdapter';
import '../settings/welcomeChannelCustomization/controllers/CustomizationSettingsCtrl';
import '../settings/welcomeChannelCustomization/directives/tbCustomizationSettingsForm';

import './controllers/AddSiteCtrl';
import './controllers/ContentSubplaceCtrl';
import './controllers/CreateOrEditScheduleCtrl';
import './controllers/DatasourcePublishResultCtrl';
import './controllers/EditTimeZoneCtrl';
import './controllers/FilterPanelCtrl';
import './controllers/LoginCtrl';
import '../revisionHistory/controllers/RevisionHistoryDialogCtrl';
import './controllers/ScheduleSpaceDetailsCtrl';
import './controllers/ServerUserSettingsSubspaceCtrl';
import './controllers/SiteUserSettingsSubspaceCtrl';
import './controllers/TimeZoneSiteSettingCtrl';

import './directives/childEventDirectives';
import './directives/keyEventDirectives';
import './directives/pixelStyleDirectives';
import './directives/tbApp';
import './directives/tbBytesInputConverter';
import './directives/tbChangePassword';
import './directives/tbDataGrid';
import './directives/tbDataGridBody';
import './directives/tbDataGridHeaders';
import './directives/tbDataSourceCertificationEditor';
import './directives/tbDataSourceNlpSettingEditor';
import './directives/tbDaysPicker';
import './directives/tbDisabled';
import './directives/tbEditableTags';
import './directives/tbEditableTextarea';
import './directives/tbEditLanguageAndLocale';
import './directives/tbEditTimeZone';
import './directives/tbEmailInputValidator';
import './directives/tbFileDrop';
import './directives/tbFilteringListBox';
import './directives/tbHref';
import './directives/tbIfFeatureFlag';
import './directives/tbLocalizeTranslate';
import './directives/tbMatchingInputsValidator';
import './directives/tbMousedownOutside';
import './directives/tbPopover';
import './directives/tbPopup';
import './directives/tbPopupMenu';
import './directives/tbPopupMenuButton';
import './directives/tbPopupMenuContent';
import './directives/tbPreventInput';
import './directives/tbRadioButton';
import './directives/tbRangeInputValidator';
import './directives/tbResetStartPage';
import './directives/tbScroll';
import './directives/tbSelect';
import './directives/tbSelected';
import './directives/tbSiteIdInputValidator.ts';
import './directives/tbSiteRolePermissionsTable';
import './directives/tbStackedElement';
import './directives/tbTagsInput';
import './directives/tbTemplateList';
import './directives/tbTextBox';
import './directives/tbTextDropdown';
import './directives/tbTextile';
import './directives/tbTimePicker';
import './directives/tbTimeZoneSiteSetting';
import './directives/tbTranscludeFn';
import './directives/tbTypeaheadFormat';
import './directives/tbUpdateSiteLogoForm';
import './directives/tbUserLimitValidator';
import './directives/tbUserNameInputValidator';
import './directives/tbVisible';
import './directives/tbViz';
import './directives/tbWindowResize';

import './factories/AngularContextFactory';
import './filters/LocalizedCountFilter';
import './filters/LocLinkifierFilter';
import './filters/PadFilter';
import './filters/RemoteRefreshScheduleTypeNameFilter';
import './filters/ScheduleFrequencyDescriptionFilter';
import '../commonInfra/utils/SizeFilter';

import './L10n/L10n';
import './L10n/LocalizeToNgTranslateAdapter';

import './models/Set';

import '../backgroundJob/types/BackgroundJobs';
import './resources/Dataroles';
import '../revisionHistoryDatasource/resources/DatasourceRevisions';
import './resources/Lenses';
import './resources/RemoteRefreshAgents';
import './resources/RemoteRefreshSchedules';
import './resources/Schedules';
import './siteManagement/services/resources/SiteNameSansIds';
import './siteManagement/services/resources/SiteNameWithIds';
import './siteManagement/services/resources/Sites';
import './resources/WorkbookAccelerations';
import '../revisionHistoryWorkbook/resources/WorkbookRevisions';

import '../app/spaces/SpacesSiteUrls';
import '../commonInfra/contentActions/ActionHelpers';
import '../commonInfra/contentActions/ActionPanelInterfaces';
import '../commonInfra/contentActions/ContentActionNotification';
import './services/ChangeDatasourceRemoteQueryAgentAction';
import './services/ChangeDatasourceRemoteRefreshPropertiesHelper';
import './services/ChangeScheduleAction';
import '../settings/services/ClearSavedRevisionsAction';
import './services/ConfirmActionDialog';
import './services/ContentUrl';
import './services/CreateOrUpdateScheduleAction';
import './services/CreateSiteAction';
import './services/DataRoleErrors';
import '../revisionHistory/services/DeleteRevisionAction';
import '~src/schedule/actions/DeleteSchedulesAction';
import './services/EditRemoteRefreshExpectedDurationAction';
import './services/EditSiteAction';
import '../userManagement/services/EditSiteMembershipAction';
import './services/ErrorMessageHandler';
import './services/PasswordPolicyService';
import '../httpClient/WebHttpClientWithErrorToast';
import '../httpClient/utils/HttpClientTestHook';
import './services/FetcherFactory';
import './services/LicensesService';
import './services/ModalService';
import './services/NotifyingFutureActionService';
import './services/PersonalAccessTokensService';
import '../zeus/PostLoginNotification';
import '../zeus/SiteExpirationNotification';
import '../revisionHistoryWorkbook/services/PreviewWorkbookRevisionAction';
import './services/PublishDatasourceAction';
import './services/RenameAction';
import './services/RestApiService';
import '../revisionHistory/services/RevisionActions';
import '../revisionHistory/services/RevisionHistoryAction';
import '../revisionHistory/services/RevisionHistoryActionAngular';
import '../schedule/actions/RunSchedulesAction';
import '../schedule/actions/RunServerSchedulesAction';
import './services/ScheduleErrors';
import './services/SchedulePermissions';
import '~src/schedule/actions/SetScheduleStatusAction';
import './services/SiteUrlMatcherFactory';
import './services/ToasterService';
import './services/UrlTelemetryService';
import './services/UserSettingsCtrlCommon';
import './services/VizService';
import '../dataAcceleration/actions/WorkbookAccelerationActions';

import 'focus-visible/dist/focus-visible.min.js';
import 'url-search-params-polyfill';

import './config';
import './ImagePreloader';

// Finally, after all the services are registered in Angular, we can bootstrap the app.
import './run';
